/**
 * Schedule Overrides Modal
 * Lists all approved overrides on a single Interest Payment row and lets the
 * user revert any one (or all). Opened from the row's flag icon.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiDelete } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';

const fmtDate = (d) => d ? format(new Date(d), 'dd MMM yyyy') : '-';

function ScheduleOverridesModal({ open, onClose, contract, scheduleEntry, onChanged }) {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null); // override id being reverted
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!contract?.id || !scheduleEntry) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiGet(`/amendments/contract/${contract.id}/overrides`);
      const all = resp?.data || resp || [];
      // Filter to overrides on this schedule row that are still approved.
      const filtered = (Array.isArray(all) ? all : [])
        .filter(o => o.schedule_entry_id === scheduleEntry.id && o.status === 'approved');
      setOverrides(filtered);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, [contract?.id, scheduleEntry]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRevert = async (overrideId) => {
    setReverting(overrideId);
    setError(null);
    try {
      await apiDelete(`/amendments/contract/${contract.id}/overrides/${overrideId}`);
      if (onChanged) onChanged();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to revert override');
    } finally {
      setReverting(null);
    }
  };

  const handleRevertAll = async () => {
    if (!window.confirm(`Revert all ${overrides.length} override${overrides.length === 1 ? '' : 's'} on this row?`)) return;
    setError(null);
    for (const o of overrides) {
      setReverting(o.id);
      try {
        await apiDelete(`/amendments/contract/${contract.id}/overrides/${o.id}`);
      } catch (err) {
        setError(err.response?.data?.message || err.message || `Failed to revert override ${o.id}`);
        setReverting(null);
        await load();
        return;
      }
    }
    setReverting(null);
    if (onChanged) onChanged();
    await load();
  };

  // Override type → display label + chip colour. Covers all four types:
  // interest_amount / capital_amount / accrual_amount (numeric old→new) and
  // payment_date (date old→new). Previously only interest_amount + payment_date
  // were handled, so capital_amount (T066) and accrual_amount (T106/T107) fell
  // through and were mislabelled "Date" with no change shown.
  const TYPE_META = {
    interest_amount: { label: 'Interest', color: 'warning' },
    capital_amount: { label: 'Capital', color: 'secondary' },
    accrual_amount: { label: 'Accrual', color: 'warning' },
    payment_interest_apportioned: { label: 'Interest', color: 'warning' },
    payment_capital_amount: { label: 'Capital', color: 'secondary' },
    payment_date: { label: 'Date', color: 'info' },
  };
  const typeMeta = (o) => TYPE_META[o.override_type] || { label: o.override_type || 'Override', color: 'default' };

  const renderChange = (o) => {
    if (o.override_type === 'payment_date') {
      return (
        <>
          <Typography variant="body2" component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
            {fmtDate(o.original_payment_date)}
          </Typography>
          <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>
            {fmtDate(o.new_payment_date)}
          </Typography>
        </>
      );
    }
    // interest_amount / capital_amount / accrual_amount — numeric old → new.
    return (
      <>
        <Typography variant="body2" component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
          R {formatNumber(parseFloat(o.original_value || 0), 2)}
        </Typography>
        <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>
          R {formatNumber(parseFloat(o.override_value || 0), 2)}
        </Typography>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Schedule Overrides on this Row</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {scheduleEntry && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="body2">
              <strong>{scheduleEntry.narration}</strong> on {fmtDate(scheduleEntry.payment_date)}
              {scheduleEntry.investor_name && <> &mdash; {scheduleEntry.investor_name}</>}
            </Typography>
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : overrides.length === 0 ? (
          <Alert severity="info">No active overrides on this row.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      <Chip
                        label={typeMeta(o).label}
                        size="small"
                        color={typeMeta(o).color}
                      />
                    </TableCell>
                    <TableCell>{renderChange(o)}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {o.reason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {fmtDate(o.created_at)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {o.created_by}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={reverting === o.id ? <CircularProgress size={14} /> : <UndoIcon />}
                        onClick={() => handleRevert(o.id)}
                        disabled={reverting !== null}
                      >
                        Revert
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {overrides.length > 1 && (
          <Button
            onClick={handleRevertAll}
            color="error"
            variant="contained"
            disabled={reverting !== null}
            startIcon={<UndoIcon />}
          >
            Revert all
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleOverridesModal;
